<template>
  <div class="providers-page">
    <base-header class="pb-6 pb-7 pt-5 pt-md-7 bg-gradient">
      <h2 class="page-title">{{ $t('providers.title') }}</h2>

      <!-- Alert messages -->
      <transition name="fade">
        <div v-if="success" class="alert alert-success mt-3" role="alert">
          <i class="ni ni-check-bold mr-2"></i>
          <strong>{{ $t('common.successAlert') }}</strong> {{ message || $t('providers.providerUpdated') }}
        </div>
      </transition>
      <transition name="fade">
        <div v-if="fail" class="alert alert-danger mt-3" role="alert">
          <i class="ni ni-notification-70 mr-2"></i>
          <strong>{{ $t('common.errorAlert') }}</strong> {{ message || $t('providers.checkData') }}
        </div>
      </transition>

      <!-- SMS Modal -->
      <b-modal :visible.sync="sms" id="modal-otp" :title="$t('providers.smsConfirm')" @ok="authProviderSms" @hide="sms = false">
        <p>{{ message }}</p>
        <b-form-group class="text-sm" :label="$t('providers.enterSmsCode')" label-for="input-otp">
          <base-input v-model="code" :placeholder="$t('providers.codePlaceholder')"></base-input>
        </b-form-group>
        <template v-slot:modal-ok>
          <span>{{ $t('common.confirm') }}</span>
        </template>
        <template v-slot:modal-cancel>
          <span>{{ $t('common.cancel') }}</span>
        </template>
      </b-modal>

      <!-- Provider Cards -->
      <b-row>
        <b-col v-for="provider in providers" :key="provider.id" xl="4" lg="6" class="mb-4">
          <div class="provider-card">
            <!-- Card Header -->
            <div class="provider-card-header">
              <div class="provider-name-wrapper">
                <input
                  v-if="editingId === provider.id"
                  :ref="'nameInput-' + provider.id"
                  type="text"
                  class="provider-name-input"
                  v-model="provider.display_name"
                  :placeholder="$t('providers.displayNamePlaceholder')"
                  autocomplete="off"
                  @keydown.enter.prevent="saveName(provider)"
                  @keydown.esc.prevent="cancelEdit(provider)"
                  @blur="saveName(provider)"
                >
                <h3
                  v-else
                  class="provider-name provider-name--editable"
                  :title="$t('providers.editNameHint')"
                  @click="startEdit(provider)"
                >
                  <template v-if="provider.display_name">{{ provider.display_name }}</template>
                  <template v-else><span class="provider-id">#{{ provider.id }}</span> {{ provider.type }}</template>
                  <i class="ni ni-ruler-pencil edit-icon"></i>
                </h3>
                <span v-if="supportsCaptureService(provider)" class="capture-badge">
                  {{ $t('providers.captureAvailable') }}
                </span>
              </div>
              <div class="header-actions">
                <button
                  type="button"
                  class="proxy-settings-btn"
                  @click="openProxySettings(provider.id)"
                  :title="$t('providers.proxySettings')"
                >
                  <i class="ni ni-settings-gear-65"></i>
                  <span>{{ $t('providers.proxy') }}</span>
                </button>
                <label
                  class="status-switch"
                :class="{ 'active': provider.is_active }"
                role="switch"
                :aria-checked="provider.is_active"
                tabindex="0"
                @click="updateProviderStatus(provider)"
                @keydown.space.prevent="updateProviderStatus(provider)"
              >
                <span class="switch-track">
                  <span class="switch-thumb"></span>
                </span>
                <span class="switch-label">
                  {{ provider.is_active ? $t('common.on') : $t('common.off') }}
                </span>
              </label>
              </div>
            </div>

            <!-- Card Body - Credentials Form -->
            <div class="provider-card-body">
              <div class="form-group">
                <label class="form-label">{{ $t('providers.login') }}</label>
                <input
                  type="text"
                  class="form-control"
                  :placeholder="$t('providers.loginPlaceholder')"
                  v-model="provider.login"
                  autocomplete="off"
                >
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('providers.password') }}</label>
                <input
                  type="password"
                  class="form-control"
                  :placeholder="$t('providers.passwordPlaceholder')"
                  v-model="provider.password"
                  autocomplete="off"
                >
              </div>
            </div>

            <!-- Card Footer - Actions -->
            <div class="provider-card-footer">
              <!-- Primary action for Oson - Capture -->
              <button
                v-if="supportsCaptureService(provider)"
                type="button"
                class="btn-action btn-capture"
                @click="openCaptureModal(provider.id)"
              >
                {{ $t('providers.captureData') }}
              </button>

              <!-- Secondary actions row -->
              <div class="secondary-actions"
                   v-if="!supportsCaptureService(provider)">
                <button
                  type="button"
                  class="btn-action btn-update"
                  @click="updateProvider(provider)"
                >
                  {{ $t('providers.save') }}
                </button>
                <button
                  type="button"
                  class="btn-action btn-auth"
                  :class="{ 'disabled': !provider.is_authable }"
                  :disabled="!provider.is_authable"
                  @click="authProvider(provider)"
                >
                  {{ $t('providers.authorize') }}
                </button>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </base-header>

    <!-- Credential Capture Modal -->
    <credential-capture-modal
      :show.sync="showCaptureModal"
      :provider-id="captureProviderId"
      @success="handleCaptureSuccess"
      @close="handleCaptureClose"
    />

    <!-- Proxy Configuration Modal -->
    <proxy-config-modal
      :show.sync="showProxyModal"
      :provider-id="selectedProviderId"
      @success="handleProxySuccess"
      @error="handleProxyError"
    />
  </div>
</template>

<script>
import CredentialCaptureModal from "@/components/Modals/CredentialCaptureModal.vue";
import ProxyConfigModal from "@/components/Modals/ProxyConfigModal.vue";
import axios from "axios"

export default {
  components: {
    CredentialCaptureModal,
    ProxyConfigModal
  },
  data() {
    return {
      providers: [],
      success: false,
      fail: false,
      sms: false,
      message: null,
      code: '',
      current_provider: null,
      showCaptureModal: false,
      captureProviderId: null,
      showProxyModal: false,
      selectedProviderId: null,
      editingId: null,
      editBuffer: ''
    };
  },
  methods: {
    async getProviders() {
      const response = await axios.get('dashboard/providers')
      this.providers = (response.data.providers || []).map(p => ({
        ...p,
        display_name: p.display_name || ''
      }))
    },
    startEdit($provider) {
      this.editBuffer = $provider.display_name || '';
      this.editingId = $provider.id;
      this.$nextTick(() => {
        const ref = this.$refs['nameInput-' + $provider.id];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) {
          el.focus();
          if (el.select) el.select();
        }
      });
    },
    saveName($provider) {
      if (this.editingId !== $provider.id) return;
      this.editingId = null;
      const trimmed = ($provider.display_name || '').trim();
      $provider.display_name = trimmed;
      if (trimmed === (this.editBuffer || '').trim()) return;
      this.updateProviderName($provider);
    },
    cancelEdit($provider) {
      $provider.display_name = this.editBuffer;
      this.editingId = null;
    },
    async updateProvider($provider) {
      let res;
      try {
        res = await axios.post("dashboard/providers", {
          id: $provider.id,
          login: $provider.login,
          password: $provider.password,
          display_name: ($provider.display_name || '').trim() || null,
          type: $provider.type
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
        }
        this.fail = true;
        this.message = null;
        setTimeout(() => this.fail = false, 5000);
        console.log(e);
        return;
      }

      if (res.data.status === true) {
        this.success = true;
        this.message = null;
        setTimeout(() => this.success = false, 5000);
      }
    },
    async updateProviderName($provider) {
      let res;
      try {
        res = await axios.post("dashboard/providers", {
          id: $provider.id,
          display_name: ($provider.display_name || '').trim() || null,
          type: $provider.type
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
        }
        this.fail = true;
        this.message = null;
        setTimeout(() => this.fail = false, 5000);
        console.log(e);
        return;
      }

      if (res.data.status === true) {
        this.success = true;
        this.message = null;
        setTimeout(() => this.success = false, 5000);
      }
    },
    async authProvider($provider) {
      let res;
      if (!$provider.is_authable) return;
      try {
        res = await axios.post("dashboard/providers/auth", {
          id: $provider.id,
          type: $provider.type
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
        }
        this.fail = true;
        this.message = null;
        setTimeout(() => this.fail = false, 5000);
        console.log(e);
        return;
      }

      if (res.data.status === true) {
        if (res.data.message !== null) {
          this.success = true;
          this.message = res.data.message;
          setTimeout(() => this.success = false, 5000);
        } else {
          this.sms = true;
          this.message = "Код отправлен на номер " + res.data.phone + ". Введите код для авторизации в течение " + res.data.expire + " секунд";
          this.current_provider = $provider;
        }
      }
    },
    async authProviderSms() {
      let res;
      if (!this.current_provider.is_authable) return;
      try {
        res = await axios.post("dashboard/providers/auth/sms", {
          id: this.current_provider.id,
          code: this.code
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
        }
        this.fail = true;
        this.message = null;
        setTimeout(() => this.fail = false, 5000);
        console.log(e);
        return;
      }

      if (res.data.status === true) {
        this.sms = false;
        this.success = true;
        this.message = "Успешно авторизован";
        setTimeout(() => this.success = false, 5000);
      }
    },
    async updateProviderStatus($provider) {
      let res;
      try {
        res = await axios.post("dashboard/providers", {
          id: $provider.id,
          is_active: !$provider.is_active,
          type: $provider.type
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
        }
        console.log(e);
        return;
      }

      if (res.data.status === true) {
        $provider.is_active = !$provider.is_active;
      }
    },
    openCaptureModal(providerId) {
      this.captureProviderId = providerId;
      this.showCaptureModal = true;
    },
    handleCaptureSuccess() {
      this.showCaptureModal = false;
      this.success = true;
      this.message = "Данные провайдера успешно обновлены через захват";
      this.getProviders();
      setTimeout(() => {
        this.success = false;
        this.message = null;
      }, 5000);
    },
    handleCaptureClose() {
      this.showCaptureModal = false;
      this.captureProviderId = null;
    },
    supportsCaptureService(provider) {
      const type = provider.type && provider.type.toLowerCase();
      return type === 'oson' || type === 'ipak' || type === 'infin';
    },
    openProxySettings(providerId) {
      this.selectedProviderId = providerId;
      this.showProxyModal = true;
    },
    handleProxySuccess(message) {
      this.showProxyModal = false;
      this.success = true;
      this.message = message;
      setTimeout(() => this.success = false, 5000);
    },
    handleProxyError(message) {
      this.fail = true;
      this.message = message;
      setTimeout(() => this.fail = false, 5000);
    }
  },
  mounted() {
    this.getProviders();
  }
};
</script>

<style scoped lang="scss">
.providers-page {
  .page-title {
    color: #fff;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  .alert {
    border: none;
    border-radius: 8px;
    font-weight: 500;
    margin-bottom: 1.5rem;

    &.alert-success {
      background-color: rgba(82, 196, 26, 0.15);
      color: #52c41a;
      border-left: 4px solid #52c41a;
    }

    &.alert-danger {
      background-color: rgba(255, 77, 79, 0.15);
      color: #ff4d4f;
      border-left: 4px solid #ff4d4f;
    }
  }
}

.provider-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  padding: 1.25rem;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #d0d0d0;
  }
}

.provider-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  .provider-name-wrapper {
    display: flex;
    flex-direction: column;
  }

  .provider-name {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
    line-height: 1.3;
  }

  .provider-id {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    color: #4361EE;
    background: rgba(67, 97, 238, 0.1);
    padding: 0.1rem 0.375rem;
    border-radius: 4px;
    margin-right: 0.25rem;
    vertical-align: middle;
  }

  .provider-name--editable {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    transition: background-color 0.15s ease;

    .edit-icon {
      font-size: 0.75rem;
      color: #c0c4cc;
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &:hover {
      background-color: rgba(67, 97, 238, 0.06);

      .edit-icon {
        opacity: 1;
      }
    }
  }

  .provider-name-input {
    width: 100%;
    max-width: 240px;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    border: 1px solid #4361EE;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    background: #fff;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.15);
    }

    &::placeholder {
      color: #c0c4cc;
      font-weight: 400;
    }
  }

  .capture-badge {
    display: block;
    font-size: 0.7rem;
    color: #11998e;
    font-weight: 500;
    margin-top: 0.25rem;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .proxy-settings-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    margin: 0 0 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #909399;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: #606266;
    }

    i {
      font-size: 0.8rem;
    }

    span {
      line-height: 1;
    }
  }

  .status-switch {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.375rem 0.625rem 0.375rem 0.5rem;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }

    .switch-track {
      position: relative;
      width: 28px;
      height: 16px;
      background-color: #e4e7ed;
      border-radius: 8px;
      transition: background-color 0.2s ease;
      flex-shrink: 0;
    }

    .switch-thumb {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 12px;
      background-color: #fff;
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .switch-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #909399;
      transition: color 0.15s ease;
      white-space: nowrap;
    }

    &.active {
      .switch-track {
        background-color: #52c41a;
      }

      .switch-thumb {
        transform: translateX(12px);
      }

      .switch-label {
        color: #52c41a;
      }
    }

    &:focus {
      outline: none;
      background-color: rgba(67, 97, 238, 0.08);
    }
  }
}

.provider-card-body {
  .form-group {
    margin-bottom: 0.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #909399;
    margin-bottom: 0.3rem;
  }

  .form-control {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fff;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #4361EE;
    }

    &::placeholder {
      color: #c0c4cc;
    }
  }
}

.provider-card-footer {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;

  .btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.875rem;
    border: none;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    background-color: transparent;

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .btn-capture {
    color: #11998e;

    &:hover {
      background-color: rgba(17, 153, 142, 0.08);
    }
  }

  .secondary-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-update {
    color: #4361EE;

    &:hover {
      background-color: rgba(67, 97, 238, 0.08);
    }
  }

  .btn-auth {
    color: #606266;

    &:hover:not(.disabled) {
      background-color: rgba(0, 0, 0, 0.04);
      color: #333;
    }
  }
}

.bg-gradient {
  background: linear-gradient(87deg, #172b4d 0, #1a174d 100%) !important;
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Responsive
@media (max-width: 576px) {
  .provider-card {
    padding: 1rem;
  }

  .provider-card-header {
    .status-switch {
      .switch-label {
        display: none;
      }
    }
  }

  .provider-card-footer {
    flex-direction: column;
    gap: 0.5rem;

    .btn-action {
      width: 100%;
      justify-content: center;
    }

    .secondary-actions {
      flex-direction: column;
      width: 100%;

      .btn-action {
        width: 100%;
      }
    }
  }
}
</style>
